/**
 * Design canvases (ADR 0011). Every position and size is a design number in
 * canvas units; nothing here is derived from a viewport. Three states:
 * portrait phone, landscape phone, wide (desktop and tablet). The numbers come
 * from the design handoff (ADR 0023): the table is a 4:3 picture, the seats sit
 * on its rail, the played cards around its middle, the hand half off the bottom.
 */
/** Phones: a flat row, the top half of each card showing, 34% of it uncovered (owner, 11 Sept). */
export const FLAT_HAND = { tiers: 1, show: 0.34, tilt: 0, tierGap: 0, raise: 0 };
/** Wide: a two-row fan, 55% of each card uncovered, so the court figures show (owner, 11 Sept). */
const WIDE_HAND = { tiers: 2, show: 0.55, tilt: 3, tierGap: 85, raise: 10 };
/** The box a seat takes: `width` wide around the circle centred at (cx, cy), pill below. */
function seatBox(cx, cy, width, pod) {
    return { x: cx - width / 2, y: cy - pod.size / 2, w: width, h: pod.size + pod.pillDrop };
}
/** Handoff pod: 150 px scaled by 0.85 on the 1000 px table. */
const WIDE_POD = { size: 128, pillDrop: 16 };
const LANDSCAPE_POD = { size: 64, pillDrop: 14 };
const PORTRAIT_POD = { size: 76, pillDrop: 14 };
/*
 * Hand rects hold the visible half of a card plus the lift of a playable one.
 * Cards grew about 10% on 11 Sept (owner: "there is room, make them bigger").
 */
export const CANVASES = {
    portrait: {
        state: "portrait",
        w: 390,
        h: 844,
        minLongAxis: 640,
        card: { w: 90, h: 126 },
        trickCard: { w: 56, h: 78 },
        pod: PORTRAIT_POD,
        handFan: FLAT_HAND,
        surface: { x: 10, y: 160, w: 370, h: 493 },
        turned: true,
        elements: {
            topBar: { x: 0, y: 0, w: 390, h: 134 },
            seatTop: seatBox(195, 199, 96, PORTRAIT_POD),
            seatLeft: seatBox(58, 406, 96, PORTRAIT_POD),
            seatRight: seatBox(332, 406, 96, PORTRAIT_POD),
            trick: { x: 110, y: 318, w: 170, h: 145 },
            seatSelf: seatBox(195, 594, 96, PORTRAIT_POD),
            band: { x: 20, y: 470, w: 350, h: 56 },
            actions: { x: 10, y: 552, w: 370, h: 210 },
            hand: { x: 0, y: 762, w: 390, h: 82 },
        },
        allowedOverlaps: [["actions", "seatSelf"]],
    },
    phoneLandscape: {
        state: "phoneLandscape",
        w: 960,
        h: 444,
        minLongAxis: 700,
        card: { w: 78, h: 108 },
        trickCard: { w: 48, h: 66 },
        pod: LANDSCAPE_POD,
        handFan: FLAT_HAND,
        surface: { x: 273, y: 70, w: 413, h: 310 },
        turned: false,
        elements: {
            topBar: { x: 0, y: 0, w: 960, h: 52 },
            seatTop: seatBox(480, 107, 130, LANDSCAPE_POD),
            seatLeft: seatBox(298, 219, 130, LANDSCAPE_POD),
            seatRight: seatBox(661, 219, 130, LANDSCAPE_POD),
            trick: { x: 405, y: 156, w: 150, h: 117 },
            seatSelf: seatBox(480, 327, 130, LANDSCAPE_POD),
            band: { x: 370, y: 190, w: 220, h: 52 },
            actions: { x: 220, y: 190, w: 520, h: 184 },
            hand: { x: 220, y: 374, w: 520, h: 70 },
        },
        // A short canvas: your panel covers the lower table while it is up.
        allowedOverlaps: [
            ["band", "trick"],
            ["actions", "seatLeft"],
            ["actions", "seatRight"],
            ["actions", "seatSelf"],
            ["actions", "trick"],
            ["actions", "band"],
        ],
    },
    wide: {
        state: "wide",
        w: 1140,
        h: 1010,
        minLongAxis: 640,
        card: { w: 156, h: 218 },
        // Table cards grew a quarter and the table moved up 30 for the two-row hand (owner, 11 Sept).
        trickCard: { w: 104, h: 145 },
        pod: WIDE_POD,
        handFan: WIDE_HAND,
        surface: { x: 70, y: 88, w: 1000, h: 750 },
        turned: false,
        elements: {
            topBar: { x: 0, y: 0, w: 1140, h: 112 },
            seatTop: seatBox(570, 186, 200, WIDE_POD),
            seatLeft: seatBox(130, 448, 200, WIDE_POD),
            seatRight: seatBox(1010, 448, 200, WIDE_POD),
            trick: { x: 440, y: 320, w: 260, h: 240 },
            seatSelf: seatBox(570, 688, 200, WIDE_POD),
            band: { x: 340, y: 408, w: 460, h: 64 },
            actions: { x: 290, y: 560, w: 560, h: 208 },
            hand: { x: 150, y: 768, w: 840, h: 242 },
        },
        allowedOverlaps: [
            ["band", "trick"],
            ["actions", "seatSelf"],
        ],
    },
};
export const ELEMENT_IDS = [
    "topBar",
    "seatTop",
    "seatLeft",
    "seatRight",
    "seatSelf",
    "trick",
    "band",
    "hand",
    "actions",
];
export function overlaps(a, b) {
    return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}
export function inside(rect, canvas) {
    return rect.x >= 0 && rect.y >= 0 && rect.x + rect.w <= canvas.w && rect.y + rect.h <= canvas.h;
}
//# sourceMappingURL=canvases.js.map